import React, { useState, useEffect } from 'react';
import { verisureService } from '../../services/api';
import { VerisureSite, VerisureCabinet } from '../../types';
import { QRCodeSVG } from 'qrcode.react';
import { VerisureQrCode } from '../../components/verisure/VerisureQrCode';
import { useAuth } from '../../context/AuthContext';
import { VerisureQrCardModal } from '../../components/verisure/VerisureQrCardModal';

export const AdminVerisurePage: React.FC = () => {
  const { user } = useAuth();
  const [sites, setSites] = useState<VerisureSite[]>([]);
  const [cabinets, setCabinets] = useState<VerisureCabinet[]>([]);
  
  const [newSiteName, setNewSiteName] = useState('');
  const [newSiteAddress, setNewSiteAddress] = useState('');
  
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [newCabinetLabel, setNewCabinetLabel] = useState('');
  const [selectedCabinetForCard, setSelectedCabinetForCard] = useState<VerisureCabinet | null>(null);

  const loadData = async () => {
    try {
      const sitesRes = (await verisureService.getSites()).data;
      setSites(sitesRes);
    } catch (error) {
      console.error('Failed to load sites', error);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSiteSelect = async (siteId: string) => {
    setSelectedSiteId(siteId);
    try {
      const cabinetsRes = (await verisureService.getCabinetsBySite(siteId)).data;
      setCabinets(cabinetsRes);
    } catch (error) {
      console.error('Failed to load cabinets', error);
    }
  };

  const handleCreateSite = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await verisureService.createSite({
        name: newSiteName,
        address: newSiteAddress,
        status: 'active'
      });
      setNewSiteName('');
      setNewSiteAddress('');
      loadData();
    } catch (error) {
      console.error('Failed to create site', error);
    }
  };

  const handleCreateCabinet = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSiteId) return;
    try {
      // In a real app, you might want to use a more robust unique ID generator
      const cabinetId = `CAB-${Math.random().toString(36).substring(2, 9).toUpperCase()}`;
      const qrCodeUrl = `${window.location.origin}/verisure/cabinet/${cabinetId}`;
      
      const created = (await verisureService.createCabinet({
        siteId: selectedSiteId,
        cabinetLabel: newCabinetLabel,
        qrCodeUrl,
        status: 'active'
      })).data;
      setNewCabinetLabel('');
      handleSiteSelect(selectedSiteId);
      if (created) {
        setSelectedCabinetForCard(created);
      }
    } catch (error) {
      console.error('Failed to create cabinet', error);
    }
  };

  if (!['admin', 'super_admin'].includes(user?.verisureRole || '') && !['super_admin'].includes(user?.role || '')) {
    return <div className="p-6 text-red-500">Access Denied. You must be a Verisure Admin.</div>;
  }

  const selectedSite = sites.find(s => s.id === selectedSiteId);

  return (
    <div className="p-3 sm:p-6 max-w-6xl mx-auto font-sans min-w-0 max-w-full overflow-hidden">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-black text-white">Verisure Site &amp; Cabinet Admin</h1>
        <p className="text-slate-400 text-xs sm:text-sm mt-1">Manage physical network sites, cabinets, and print Verisure QR Identity tags.</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8">
        {/* Sites Section */}
        <div className="bg-slate-900 p-4 sm:p-6 rounded-3xl border border-slate-800 shadow-xl">
          <h2 className="text-lg sm:text-xl font-bold text-white mb-4">Sites</h2>
          
          <form onSubmit={handleCreateSite} className="mb-6 bg-slate-950 p-4 rounded-2xl border border-slate-800">
            <h3 className="font-bold text-sm text-slate-300 mb-2">Create New Site</h3>
            <input
              type="text"
              placeholder="Site Name"
              value={newSiteName}
              onChange={(e) => setNewSiteName(e.target.value)}
              className="w-full mb-2 px-3 py-2 bg-slate-900 border border-slate-700 text-white rounded-xl text-sm focus:outline-none focus:border-orange-500"
              required
            />
            <input
              type="text"
              placeholder="Site Address"
              value={newSiteAddress}
              onChange={(e) => setNewSiteAddress(e.target.value)}
              className="w-full mb-3 px-3 py-2 bg-slate-900 border border-slate-700 text-white rounded-xl text-sm focus:outline-none focus:border-orange-500"
              required
            />
            <button type="submit" className="bg-gradient-to-r from-red-600 to-orange-500 text-white font-bold px-4 py-2 rounded-xl text-xs hover:opacity-90 shadow-md">
              Create Site
            </button>
          </form>

          <ul className="divide-y divide-slate-800">
            {sites.map(site => (
              <li 
                key={site.id} 
                className={`p-3 cursor-pointer rounded-xl transition ${selectedSiteId === site.id ? 'bg-orange-500/15 border border-orange-500/30' : 'hover:bg-slate-800'}`}
                onClick={() => handleSiteSelect(site.id!)}
              >
                <div className="font-bold text-white">{site.name}</div>
                <div className="text-xs text-slate-400">{site.address}</div>
              </li>
            ))}
            {sites.length === 0 && <div className="text-slate-500 text-sm py-4 text-center">No sites found.</div>}
          </ul>
        </div>

        {/* Cabinets Section */}
        <div className="bg-slate-900 p-6 rounded-3xl border border-slate-800 shadow-xl">
          <h2 className="text-xl font-bold text-white mb-4">Cabinets for Selected Site</h2>
          
          {!selectedSiteId ? (
            <div className="text-slate-500 text-sm py-8 text-center bg-slate-950 rounded-2xl border border-slate-800 border-dashed">Select a site to view/manage cabinets.</div>
          ) : (
            <>
              <form onSubmit={handleCreateCabinet} className="mb-6 bg-slate-950 p-4 rounded-2xl border border-slate-800">
                <h3 className="font-bold text-sm text-slate-300 mb-2">Add New Cabinet</h3>
                <input
                  type="text"
                  placeholder="Cabinet Label (e.g., Main Hall MDF)"
                  value={newCabinetLabel}
                  onChange={(e) => setNewCabinetLabel(e.target.value)}
                  className="w-full mb-3 px-3 py-2 bg-slate-900 border border-slate-700 text-white rounded-xl text-sm focus:outline-none focus:border-orange-500"
                  required
                />
                <button type="submit" className="bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 text-white font-bold px-4 py-2 rounded-xl text-xs hover:opacity-90 shadow-md">
                  Add Cabinet & Generate QR Card
                </button>
              </form>

              <div className="space-y-4">
                {cabinets.map(cabinet => (
                  <div key={cabinet.id} className="bg-slate-950 border border-slate-800 p-4 rounded-2xl flex items-start gap-4 hover:border-orange-500/40 transition">
                    <div className="flex-1">
                      <h4 className="font-bold text-white text-base">{cabinet.cabinetLabel}</h4>
                      <p className="text-xs text-slate-400 font-mono mt-1">ID: {cabinet.id}</p>
                      <p className="text-xs text-orange-400 mt-1 break-all">
                        <a href={cabinet.qrCodeUrl} target="_blank" rel="noreferrer">{cabinet.qrCodeUrl}</a>
                      </p>
                    </div>
                    <div className="flex flex-col items-center">
                      <div 
                        onClick={() => setSelectedCabinetForCard(cabinet)}
                        className="bg-white p-2.5 border border-orange-500/30 rounded-xl cursor-pointer hover:shadow-lg transition text-center shadow-sm"
                        title="Click to view full Verisure QR Identity Card"
                      >
                        <div className="text-[10px] font-black text-slate-900 leading-none mb-1">
                          I am <span className="text-[#ff5a00] font-black">Verisure</span>
                        </div>
                        <VerisureQrCode value={cabinet.qrCodeUrl} size={90} level="H" showCenterBadge={true} badgeTheme="light" />
                      </div>
                      <button 
                        onClick={() => setSelectedCabinetForCard(cabinet)}
                        className="text-xs text-orange-400 font-bold mt-2 hover:text-orange-300 underline flex items-center gap-1"
                      >
                        QR Card Badge
                      </button>
                    </div>
                  </div>
                ))}
                {cabinets.length === 0 && <div className="text-slate-500 text-sm py-4 text-center">No cabinets in this site.</div>}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Verisure QR Identity Card Modal */}
      {selectedCabinetForCard && (
        <VerisureQrCardModal
          isOpen={Boolean(selectedCabinetForCard)}
          onClose={() => setSelectedCabinetForCard(null)}
          value={selectedCabinetForCard.qrCodeUrl}
          assetId={selectedCabinetForCard.id}
          title={selectedCabinetForCard.cabinetLabel}
          subtitle={selectedSite ? `Site: ${selectedSite.name}` : 'Core Cabinet'}
        />
      )}
    </div>
  );
};
